import { readCatalogCache, writeCatalogCache } from "@/lib/catalogCache";

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah, FASMART_CATS, branchPrice, statusInfo } from "@/lib/format";
import FinanceTab from "@/components/FinanceTab";
import ImportExcel from "@/components/ImportExcel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { X, Search, Printer } from "lucide-react";

const ORDER_FLOW = ["baru", "cek_transfer", "diterima", "disiapkan", "akan_dikirim", "dikirim", "selesai"];
const NEXT_STATUS_LABEL = { cek_transfer: "💳 Cek Transferan", diterima: "✅ Terima Pesanan", disiapkan: "🧑‍🍳 Menyiapkan Pesanan", akan_dikirim: "📦 Akan dikirim", dikirim: "🛵 Sedang dikirim", selesai: "✔️ Pesanan Selesai", dibatalkan: "❌ Batalkan" };

const TITLES = { ringkasan: "📊 Ringkasan", pesanan: "🧾 Pesanan", produk: "🏷 Produk", pelanggan: "👥 Pelanggan", laporan: "📈 Laporan", keuangan: "💰 Keuangan", pricetag: "🖨 Print Price Tag", import: "📥 Import Excel", closing: "🔒 Closing" };
const STATUS_COLOR = { baru: "bg-sky-100 text-sky-700", cek_transfer: "bg-amber-100 text-amber-700", diterima: "bg-emerald-100 text-emerald-700", disiapkan: "bg-emerald-100 text-emerald-700", akan_dikirim: "bg-indigo-100 text-indigo-700", dikirim: "bg-indigo-100 text-indigo-700", selesai: "bg-stone-100 text-stone-500", dibatalkan: "bg-rose-100 text-rose-600" };

function Ringkasan() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  const cards = [
    ["Total Pendapatan Online", rupiah(s.total_revenue)], ["Pesanan Aktif", s.active_orders],
    ["Pesanan Selesai", s.completed_orders], ["Total Pelanggan", s.total_customers], ["Total Produk", s.total_products],
  ];
  return (
    <div data-testid="panel-ringkasan" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl border border-[#E5DECF] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-stone-400">{l}</div>
            <div className="font-display font-black text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <h3 className="font-display font-black text-sm mb-2">🏆 Produk Terlaris (Online)</h3>
        <div className="space-y-1 text-xs">
          {s.top_products?.map((p, i) => (
            <div key={p.name} className="flex justify-between border-b border-dashed pb-1"><span>{i + 1}. {p.name}</span><b>{p.qty} terjual</b></div>
          ))}
          {!s.top_products?.length && <p className="text-stone-400">Belum ada data.</p>}
        </div>
      </div>
    </div>
  );
}

function Pesanan({ branchId }) {
  const [orders, setOrders] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [proofs, setProofs] = useState({});
  const [uploading, setUploading] = useState(null);
  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status, proof) => {
    setUpdating(id);
    try {
      await api.patch(`/orders/${id}/status`, { status, ...(proof ? { handover_photo_url: proof } : {}) });
      toast.success(`Status diperbarui: ${statusInfo(status).label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal update status"); }
    finally { setUpdating(null); }
  };
  const uploadProof = async (id, file) => {
    if (!file) return;
    setUploading(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/design", fd);
      setProofs((p) => ({ ...p, [id]: r.data.path }));
      toast.success('Bukti terupload — pilih "Pesanan Selesai"');
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload bukti"); }
    finally { setUploading(null); }
  };
  const changeStatus = (o, next) => {
    if (next === "diterima" && !window.confirm("Crosscheck dulu: pastikan pembayaran/transferan konsumen SUDAH MASUK sebelum menerima pesanan. Lanjutkan?")) return;
    if (next === "selesai") {
      const proof = proofs[o.id] || o.handover_photo_url;
      if (!proof) { toast.error("Upload bukti foto serah terima dulu"); return; }
      return updateStatus(o.id, next, proof);
    }
    updateStatus(o.id, next);
  };

  if (!orders) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  const list = branchId ? orders.filter((o) => o.branch_id === branchId) : orders;
  return (
    <div data-testid="panel-pesanan" className="space-y-1.5">
      {!list.length && <p className="text-sm text-stone-400 p-4 bg-white rounded-2xl border border-[#E5DECF]">Belum ada pesanan online.</p>}
      {list.slice(0, 50).map((o, i) => {
        const idx = ORDER_FLOW.indexOf(o.status);
        return (
          <div key={o.id} data-testid={`panel-order-${i}`} className="bg-white rounded-xl border border-[#E5DECF] p-3 text-xs space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{o.customer_name || "Pelanggan"} · {rupiah(o.total)}</div>
                <div className="text-stone-400 truncate">{o.items?.length || 0} item · {new Date(o.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} WIB{o.delivery_slot ? ` · ${o.delivery_slot}` : ""}</div>
              </div>
              <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status] || "bg-stone-100 text-stone-500"}`}>{o.status}</span>
            </div>
            {o.payment_method === "transfer" && o.transfer_amount > 0 && (
              <div data-testid={`panel-order-transfer-${o.id}`} className="text-[11px] font-bold text-sky-700">🏦 Transfer BRI: {rupiah(o.transfer_amount)} · kode unik <span className="font-mono">{String(o.transfer_code).padStart(3, "0")}</span></div>
            )}
            {idx !== -1 && (
              <Select value="" onValueChange={(v) => changeStatus(o, v)} disabled={updating === o.id}>
                <SelectTrigger data-testid={`panel-order-action-${o.id}`} className="h-8 text-xs">
                  <SelectValue placeholder={updating === o.id ? "Menyimpan…" : "Ubah status…"} />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_FLOW.slice(idx + 1).map((k) => <SelectItem key={k} value={k} data-testid={`panel-order-opt-${o.id}-${k}`}>{NEXT_STATUS_LABEL[k]}</SelectItem>)}
                  <SelectItem value="dibatalkan" data-testid={`panel-order-opt-${o.id}-dibatalkan`}>{NEXT_STATUS_LABEL.dibatalkan}</SelectItem>
                </SelectContent>
              </Select>
            )}
            {o.status === "dikirim" && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-violet-50 border border-violet-200 px-2.5 py-2">
                <span className="text-[10px] font-semibold text-violet-800">Bukti serah terima (wajib untuk selesai):</span>
                <input type="file" accept="image/*" data-testid={`panel-order-proof-input-${o.id}`} onChange={(e) => uploadProof(o.id, e.target.files?.[0])} disabled={uploading === o.id} className="text-[10px]" />
                {uploading === o.id && <span className="text-[10px] text-stone-500">Mengupload…</span>}
                {(proofs[o.id] || o.handover_photo_url) && <span data-testid={`panel-order-proof-ok-${o.id}`} className="text-[10px] text-emerald-700 font-bold">✓ Terupload</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Produk({ branchId }) {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/products").then((r) => { writeCatalogCache(r.data); setProducts(r.data.filter((p) => FASMART_CATS.includes(p.category))) }).catch(() => setProducts(readCatalogCache()?.filter((p) => FASMART_CATS.includes(p.category)) || [])); }, []);
  if (!products) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  const list = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="panel-produk" className="space-y-2">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="panel-produk-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="w-full text-xs border border-[#E5DECF] rounded-full pl-8 pr-8 py-2 bg-white" />
        {q && <button data-testid="panel-produk-search-clear" onClick={() => setQ("")} title="Hapus pencarian" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition"><X className="w-3 h-3" /></button>}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5DECF] divide-y divide-stone-100 max-h-[60vh] overflow-y-auto">
        {list.map((p) => {
          const stock = p.stock_by_branch?.[branchId] ?? p.stock ?? 0;
          const price = p.price_by_branch?.[branchId] ?? p.price;
          return (
            <div key={p.id} data-testid={`panel-product-${p.id}`} className="flex items-center gap-2 px-3 py-2 text-xs">
              <span className="flex-1 min-w-0 truncate font-semibold">{p.name}</span>
              <span className="text-stone-500">{rupiah(price)}</span>
              <span className={`shrink-0 font-bold px-2 py-0.5 rounded-full ${stock > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>stok {stock}</span>
            </div>
          );
        })}
        {!list.length && <p className="text-xs text-stone-400 p-3">Tidak ditemukan.</p>}
      </div>
    </div>
  );
}

function Pelanggan() {
  const [customers, setCustomers] = useState(null);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([])); }, []);
  if (!customers) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  return (
    <div data-testid="panel-pelanggan" className="bg-white rounded-2xl border border-[#E5DECF] divide-y divide-stone-100 max-h-[65vh] overflow-y-auto">
      {customers.map((c) => (
        <div key={c.user_id} data-testid={`panel-customer-${c.user_id}`} className="flex items-center gap-2 px-3 py-2 text-xs">
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{c.name}</div>
            <div className="text-stone-400 truncate">{c.phone || c.email}</div>
          </div>
          <span className="text-stone-500 shrink-0">{c.tier || "Warga"}</span>
          <b className="shrink-0 text-emerald-700">{c.poin_berkah || 0} poin</b>
        </div>
      ))}
      {!customers.length && <p className="text-xs text-stone-400 p-3">Belum ada pelanggan.</p>}
    </div>
  );
}

function Laporan() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  const cards = [["Kunjungan Hari Ini", d.visits_today], ["Total Kunjungan", d.total_visits], ["Pengunjung Unik", d.unique_visitors], ["Total Login", d.total_logins], ["Pelanggan Terdaftar", d.customers]];
  return (
    <div data-testid="panel-laporan" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl border border-[#E5DECF] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-stone-400">{l}</div>
            <div className="font-display font-black text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <h3 className="font-display font-black text-sm mb-2">🔎 Pencarian Populer</h3>
        <div className="space-y-1 text-xs">
          {(d.popular_searches || []).slice(0, 8).map((s) => (
            <div key={s.term} className="flex justify-between border-b border-dashed pb-1"><span>{s.term}</span><b>{s.count}x</b></div>
          ))}
          {!d.popular_searches?.length && <p className="text-stone-400">Belum ada data.</p>}
        </div>
      </div>
    </div>
  );
}

function PriceTag({ branchId }) {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  useEffect(() => { api.get("/products").then((r) => { writeCatalogCache(r.data); setProducts(r.data.filter((p) => FASMART_CATS.includes(p.category))) }).catch(() => setProducts(readCatalogCache()?.filter((p) => FASMART_CATS.includes(p.category)) || [])); }, []);
  const buildPrintRoot = () => {
    const src = document.getElementById("pricetag-area");
    if (!src) return;
    document.getElementById("pricetag-print-root")?.remove();
    const root = document.createElement("div");
    root.id = "pricetag-print-root";
    root.className = "pricetag-grid";
    root.innerHTML = src.innerHTML;
    document.body.appendChild(root);
    document.body.classList.add("printing-tags");
  };
  const removePrintRoot = () => {
    document.getElementById("pricetag-print-root")?.remove();
    document.body.classList.remove("printing-tags");
  };
  useEffect(() => {
    window.addEventListener("beforeprint", buildPrintRoot);
    window.addEventListener("afterprint", removePrintRoot);
    window.addEventListener("pointerdown", removePrintRoot);
    return () => {
      window.removeEventListener("beforeprint", buildPrintRoot);
      window.removeEventListener("afterprint", removePrintRoot);
      window.removeEventListener("pointerdown", removePrintRoot);
      removePrintRoot();
    };
  }, []);
  const doPrint = () => window.print();
  if (!products) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  const list = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const selProducts = products.filter((p) => selected[p.id]);
  const allFilteredSelected = list.length > 0 && list.every((p) => selected[p.id]);
  const toggleAll = () => {
    const next = { ...selected };
    list.forEach((p) => { next[p.id] = !allFilteredSelected; });
    setSelected(next);
  };
  return (
    <div data-testid="panel-pricetag" className="space-y-3">
      <style>{`
        .pricetag-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(52mm, 1fr)); gap: 3mm; }
        .pricetag { border: 1.5px dashed #94a3b8; border-radius: 2mm; padding: 3mm 2mm; text-align: center; break-inside: avoid; background: white; }
        .pricetag-shop { font-size: 7pt; letter-spacing: 2px; font-weight: 800; color: #065f46; }
        .pricetag-name { font-size: 8.5pt; font-weight: 700; min-height: 9mm; display: flex; align-items: center; justify-content: center; margin: 1mm 0; }
        .pricetag-price { font-size: 15pt; font-weight: 900; color: #064e3b; }
        @media print {
          body.printing-tags > *:not(#pricetag-print-root) { display: none !important; }
          body.printing-tags #pricetag-print-root { position: static !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
        }
      `}</style>
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-3 space-y-2 no-print">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="pricetag-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="w-full text-xs border border-[#E5DECF] rounded-full pl-8 pr-8 py-2 bg-white" />
            {q && <button data-testid="pricetag-search-clear" onClick={() => setQ("")} title="Hapus pencarian" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition p-0.5"><X className="w-3 h-3" /></button>}
          </div>
          <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer">
            <input data-testid="pricetag-select-all" type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="w-4 h-4 accent-emerald-700" /> Pilih Semua ({list.length})
          </label>
          <button data-testid="pricetag-print-btn" onClick={doPrint} disabled={!selProducts.length} className="px-4 py-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
            <Printer className="w-3.5 h-3.5" /> Print {selProducts.length ? `(${selProducts.length} tag)` : ""}
          </button>
        </div>
        <div className="text-[11px] text-stone-400">Terpilih: <b data-testid="pricetag-count">{selProducts.length}</b> produk · harga mengikuti cabang yang dipilih di kasir</div>
        <div className="max-h-56 overflow-y-auto divide-y divide-stone-100">
          {list.map((p) => (
            <label key={p.id} data-testid={`pricetag-item-${p.id}`} className="flex items-center gap-2 py-1.5 text-xs cursor-pointer hover:bg-stone-50 px-1 rounded">
              <input type="checkbox" checked={!!selected[p.id]} onChange={() => setSelected({ ...selected, [p.id]: !selected[p.id] })} className="w-4 h-4 accent-emerald-700 shrink-0" />
              <span className="flex-1 min-w-0 truncate">{p.name}</span>
              <b>{rupiah(branchPrice(p, branchId))}</b>
            </label>
          ))}
        </div>
      </div>
      {selProducts.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5DECF] p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mb-2 no-print">Preview label (yang tercetak hanya area ini)</div>
          <div id="pricetag-area" className="pricetag-grid" data-testid="pricetag-preview">
            {selProducts.map((p) => (
              <div key={p.id} className="pricetag">
                <div className="pricetag-shop">FASMART</div>
                <div className="pricetag-name">{p.name}</div>
                <div className="pricetag-price">{rupiah(branchPrice(p, branchId))}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClosingPanel({ branchId, branchName }) {
  const [cur, setCur] = useState(null);
  const [hist, setHist] = useState([]);
  const [actual, setActual] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const buildClosingPrintRoot = () => {
    const src = document.getElementById("closing-print-area");
    if (!src) return;
    document.getElementById("closing-print-root")?.remove();
    const root = document.createElement("div");
    root.id = "closing-print-root";
    root.className = "font-mono text-stone-800";
    root.innerHTML = src.innerHTML;
    document.body.appendChild(root);
    document.body.classList.add("printing-closing");
  };
  const removeClosingPrintRoot = () => {
    document.getElementById("closing-print-root")?.remove();
    document.body.classList.remove("printing-closing");
  };
  useEffect(() => {
    window.addEventListener("beforeprint", buildClosingPrintRoot);
    window.addEventListener("afterprint", removeClosingPrintRoot);
    window.addEventListener("pointerdown", removeClosingPrintRoot);
    return () => {
      window.removeEventListener("beforeprint", buildClosingPrintRoot);
      window.removeEventListener("afterprint", removeClosingPrintRoot);
      window.removeEventListener("pointerdown", removeClosingPrintRoot);
      removeClosingPrintRoot();
    };
  }, []);

  const load = () => {
    api.get(`/admin/closing/current?branch_id=${branchId}`).then((r) => setCur(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat data closing"));
    api.get(`/admin/closing/history?branch_id=${branchId}`).then((r) => setHist(r.data)).catch(() => {});
  };
  useEffect(() => { if (branchId) load(); }, [branchId]);

  const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
  const selisih = actual !== "" && cur ? (Number(actual) || 0) - cur.expected_cash : null;
  const doClose = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/closing", { branch_id: branchId, actual_cash: Number(actual) || 0, note });
      toast.success(r.data.selisih === 0 ? "Tutup buku tersimpan — laci PAS ✓" : `Tutup buku tersimpan — selisih ${rupiah(r.data.selisih)}`);
      setActual(""); setNote(""); setConfirming(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan closing"); }
    finally { setBusy(false); }
  };

  if (!cur) return <p className="text-sm text-stone-400 p-4">Memuat…</p>;
  return (
    <div data-testid="panel-closing" className="space-y-4">
      <style>{`@media print { body.printing-closing > *:not(#closing-print-root) { display: none !important; } #closing-print-root { position: static !important; width: 100% !important; font-size: 9px !important; line-height: 1.3 !important; } #closing-print-root * { font-size: 9px !important; line-height: 1.3 !important; } }`}</style>

      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-display font-black text-sm">📖 Periode Berjalan</h3>
          <span className="text-[11px] text-stone-500" data-testid="closing-period-start">Sejak: {cur.period_start ? fmtDT(cur.period_start) : "Awal (belum pernah closing)"}</span>
        </div>
        <div className="text-[11px] text-stone-500 mb-2" data-testid="closing-tx-count">{cur.tx_count} transaksi kasir pada periode ini</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Object.entries(cur.by_method).map(([m, v]) => (
            <span key={m} className="px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 text-[11px] font-bold" data-testid={`closing-method-${m}`}>{m === "tunai" ? "💵" : "💳"} {m}: {rupiah(v)}</span>
          ))}
          {!Object.keys(cur.by_method).length && <span className="text-[11px] text-stone-400">Belum ada transaksi periode ini.</span>}
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">💵 Uang di Laci Seharusnya (versi sistem)</div>
          <div className="font-display font-black text-3xl text-emerald-800 mt-1" data-testid="closing-expected-cash">{rupiah(cur.expected_cash)}</div>
          <div className="text-[10px] text-emerald-600 mt-1">Semua pemasukan kasir termasuk QRIS & transfer</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4 space-y-2.5">
        <h3 className="font-display font-black text-sm">🔒 Tutup Buku (Closing)</h3>
        <p className="text-[11px] text-stone-500">Hitung uang fisik di laci, lalu masukkan jumlahnya. Setelah tutup buku, periode berikutnya dihitung baru dari nol.</p>
        <div className="flex flex-wrap gap-2">
          <input data-testid="closing-actual-input" type="number" min="0" value={actual} onChange={(e) => { setActual(e.target.value); setConfirming(false); }} placeholder="Uang fisik di laci (Rp)" className="flex-1 min-w-[170px] border border-[#E5DECF] rounded-full px-4 py-2 text-sm font-bold" />
          <input data-testid="closing-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" className="flex-1 min-w-[140px] border border-[#E5DECF] rounded-full px-4 py-2 text-sm" />
        </div>
        {selisih !== null && (
          <div data-testid="closing-selisih-preview" className={`text-sm font-black rounded-xl px-3 py-2 ${selisih === 0 ? "bg-emerald-50 text-emerald-700" : selisih < 0 ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
            Selisih: {selisih === 0 ? "PAS ✓ (fisik = sistem)" : `${selisih > 0 ? "LEBIH" : "KURANG"} ${rupiah(Math.abs(selisih))}`}
          </div>
        )}
        {!confirming ? (
          <button data-testid="closing-submit-btn" disabled={actual === ""} onClick={() => setConfirming(true)} className="w-full py-2.5 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-emerald-700 transition disabled:opacity-40">🔒 Tutup Buku Sekarang</button>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="closing-confirm-box">
            <p className="text-xs font-bold text-amber-800 mb-2">Yakin tutup buku periode ini? Sistem {rupiah(cur.expected_cash)} · {cur.tx_count} transaksi · fisik {rupiah(Number(actual) || 0)}. Setelah ini, penghitungan mulai dari nol lagi.</p>
            <div className="flex gap-2">
              <button data-testid="closing-confirm-cancel" onClick={() => setConfirming(false)} className="flex-1 py-2 rounded-full border border-[#E5DECF] text-stone-600 text-xs font-bold">Batal</button>
              <button data-testid="closing-confirm-yes" disabled={busy} onClick={doClose} className="flex-1 py-2 rounded-full bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 disabled:opacity-50">{busy ? "Menyimpan…" : "Ya, Tutup Buku"}</button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-display font-black text-sm">🗂 Riwayat Closing</h3>
          <button data-testid="closing-print-btn" onClick={() => window.print()} className="px-3 py-1.5 rounded-full bg-stone-900 text-white text-[11px] font-bold flex items-center gap-1 hover:bg-emerald-700 transition"><Printer className="w-3.5 h-3.5" /> Print Riwayat</button>
        </div>
        <div id="closing-print-area">
          <div className="hidden print:block text-center mb-2">
            <div className="font-black">{branchName} — Riwayat Closing / Tutup Buku</div>
            <div>Dicetak {new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} WIB</div>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]">
              <th className="py-1.5 pr-2">Tgl Tutup</th><th className="py-1.5 pr-2">Struk</th><th className="py-1.5 pr-2">Omzet Sistem</th><th className="py-1.5 pr-2">Uang Fisik</th><th className="py-1.5 pr-2">Selisih</th><th className="py-1.5">Penutup</th>
            </tr></thead>
            <tbody>
              {hist.map((c) => (
                <tr key={c.id} data-testid={`closing-row-${c.id}`} className="border-b border-stone-100 last:border-0">
                  <td className="py-1.5 pr-2 font-semibold">{fmtDT(c.closed_at)}</td>
                  <td className="py-1.5 pr-2">{c.tx_count}</td>
                  <td className="py-1.5 pr-2">{rupiah(c.expected_cash)}</td>
                  <td className="py-1.5 pr-2">{rupiah(c.actual_cash)}</td>
                  <td className={`py-1.5 pr-2 font-bold ${c.selisih === 0 ? "text-emerald-700" : c.selisih < 0 ? "text-rose-700" : "text-amber-700"}`} data-testid={`closing-selisih-${c.id}`}>{c.selisih === 0 ? "Pas ✓" : `${c.selisih > 0 ? "+" : ""}${rupiah(c.selisih)}`}</td>
                  <td className="py-1.5 text-stone-500">{(c.closed_by || "").split("@")[0]}{c.note ? ` · ${c.note}` : ""}</td>
                </tr>
              ))}
              {!hist.length && <tr><td colSpan={6} className="py-4 text-center text-stone-400">Belum ada riwayat closing.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function KasirPanel({ section, branchId, branchName, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#F6F1E7] overflow-y-auto" data-testid="kasir-panel">
      <div className="sticky top-0 z-10 bg-[#F6F1E7]/95 backdrop-blur border-b border-[#E5DECF]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <h2 className="font-display font-black text-lg">{TITLES[section]} <span className="text-xs font-semibold text-stone-400">· {branchName}</span></h2>
          <button data-testid="kasir-panel-close" onClick={onClose} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs font-bold"><X className="w-3.5 h-3.5" /> Kembali ke Kasir</button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto p-4 pb-10">
        {section === "ringkasan" && <Ringkasan />}
        {section === "pesanan" && <Pesanan branchId={branchId} />}
        {section === "produk" && <Produk branchId={branchId} />}
        {section === "pelanggan" && <Pelanggan />}
        {section === "laporan" && <Laporan />}
        {section === "keuangan" && <FinanceTab />}
        {section === "pricetag" && <PriceTag branchId={branchId} />}
        {section === "closing" && <ClosingPanel branchId={branchId} branchName={branchName} />}
        {section === "import" && <ImportExcel branchId={branchId} branchName={branchName} />}
      </div>
    </div>
  );
}
